import { apiFetch } from "@/lib/api";
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Code,
  Database,
  CheckCircle,
  Settings,
  Zap
} from 'lucide-react';

const DTCManagementSection = () => {
  const [dtcCodes, setDtcCodes] = useState([]);
  const [filteredCodes, setFilteredCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [systemFilter, setSystemFilter] = useState('all');
  const [selectedDTC, setSelectedDTC] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  // DTC Form using React Hook Form
  const dtcForm = useForm({
    defaultValues: {
      code: '',
      description: '',
      system: 'Engine',
      severity: 'Medium',
      symptoms: '',
      possible_causes: '',
      recommended_actions: ''
    }
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // System categories for DTC codes
  const systemCategories = [
    'Engine',
    'Transmission', 
    'ABS',
    'Airbag',
    'Body Control',
    'Climate Control',
    'Exhaust',
    'Fuel System',
    'Ignition',
    'Emissions'
  ];

  // Fetch DTC codes
  const fetchDTCCodes = async () => {
    try {
      const response = await apiFetch(`${backendUrl}/api/dtc-codes`);
      if (response.ok) {
        const codes = await response.json();
        setDtcCodes(codes);
        setFilteredCodes(codes);
      } else {
        console.error('Failed to fetch DTC codes');
      }
    } catch (error) {
      console.error('Error fetching DTC codes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDTCCodes();
  }, []);

  // Filter DTC codes based on search and system filter
  useEffect(() => {
    let filtered = dtcCodes;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(dtc =>
        dtc.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dtc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dtc.system?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // System filter
    if (systemFilter !== 'all') {
      filtered = filtered.filter(dtc => dtc.system === systemFilter);
    }

    setFilteredCodes(filtered);
  }, [dtcCodes, searchTerm, systemFilter]);

  // Handle adding new DTC code using React Hook Form
  const handleAddDTC = dtcForm.handleSubmit(async (data) => {
    if (!data.code.trim() || !data.description.trim()) {
      toast.error("Please fill in DTC code and description");
      return;
    }

    try {
      const response = await apiFetch(`${backendUrl}/api/dtc-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const newDTC = await response.json();
        
        // Clear form and close dialog using React Hook Form
        dtcForm.reset();
        setShowAddDialog(false);
        fetchDTCCodes(); // Refresh the list
        
        toast.success(`DTC code ${newDTC.dtc_code.code} added successfully!`);
      } else if (response.status === 409) {
        toast.error('DTC code already exists');
      } else {
        toast.error('Failed to add DTC code');
      }
    } catch (error) {
      console.error('Error adding DTC code:', error);
      toast.error('Error adding DTC code');
    }
  });

  // Handle DTC deletion
  const handleDeleteDTC = async (dtcCode) => {
    if (window.confirm(`Are you sure you want to delete DTC code ${dtcCode}?`)) {
      try {
        const response = await apiFetch(`${backendUrl}/api/dtc-codes/${dtcCode}`, {
          method: 'DELETE',
        });
        
        if (response.ok) {
          fetchDTCCodes(); // Refresh the list
        } else {
          console.error('Failed to delete DTC code');
          alert('Failed to delete DTC code');
        }
      } catch (error) {
        console.error('Error deleting DTC code:', error);
        alert('Error deleting DTC code');
      }
    }
  };

  // Get system icon
  const getSystemIcon = (system) => {
    switch (system?.toLowerCase()) {
      case 'engine': return Settings;
      case 'transmission': return Settings;
      case 'abs': return AlertTriangle;
      case 'airbag': return AlertTriangle;
      case 'emissions': return Zap;
      default: return Code;
    }
  };

  // Get system color
  const getSystemColor = (system) => {
    switch (system?.toLowerCase()) {
      case 'engine': return 'bg-red-100 text-red-800';
      case 'transmission': return 'bg-blue-100 text-blue-800';
      case 'abs': return 'bg-orange-100 text-orange-800';
      case 'airbag': return 'bg-purple-100 text-purple-800';
      case 'emissions': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading DTC codes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">DTC Code Management</h2>
          <p className="text-muted-foreground">Manage Diagnostic Trouble Codes for enhanced vehicle problem tracking</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus size={16} />
              Add New DTC Code
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New DTC Code</DialogTitle>
              <DialogDescription>
                Create a comprehensive diagnostic trouble code with detailed information for enhanced vehicle problem diagnosis.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddDTC} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code">DTC Code *</Label>
                  <Input
                    id="code"
                    {...dtcForm.register('code', {
                      required: 'DTC code is required',
                      onChange: (e) => {
                        e.target.value = e.target.value.toUpperCase();
                      }
                    })}
                    placeholder="P0171"
                    className="uppercase"
                  />
                  {dtcForm.formState.errors.code && (
                    <p className="text-red-500 text-sm mt-1">{dtcForm.formState.errors.code.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="system">System *</Label>
                  <Controller
                    control={dtcForm.control}
                    name="system"
                    rules={{ required: 'System is required' }}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {systemCategories.map(system => (
                            <SelectItem key={system} value={system}>{system}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {dtcForm.formState.errors.system && (
                    <p className="text-red-500 text-sm mt-1">{dtcForm.formState.errors.system.message}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  {...dtcForm.register('description', {
                    required: 'Description is required'
                  })}
                  placeholder="System Too Lean (Bank 1)"
                />
                {dtcForm.formState.errors.description && (
                  <p className="text-red-500 text-sm mt-1">{dtcForm.formState.errors.description.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="symptoms">Common Symptoms</Label>
                <Textarea
                  id="symptoms"
                  {...dtcForm.register('symptoms')}
                  placeholder="Rough idle, poor fuel economy, check engine light..."
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="possible_causes">Possible Causes</Label>
                <Textarea
                  id="possible_causes"
                  {...dtcForm.register('possible_causes')}
                  placeholder="Vacuum leak, faulty MAF sensor, fuel pump issues..."
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="recommended_actions">Recommended Actions</Label>
                <Textarea
                  id="recommended_actions"
                  {...dtcForm.register('recommended_actions')}
                  placeholder="Check vacuum lines, test MAF sensor, inspect fuel system..."
                  rows={2}
                />
              </div>

              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1">Add DTC Code</Button>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total DTC Codes</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dtcCodes.length}</div>
            <p className="text-xs text-muted-foreground">Available codes</p>
          </CardContent>
        </Card>

        {['Engine', 'Transmission', 'ABS', 'Emissions'].map(system => (
          <Card key={system}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{system} Codes</CardTitle>
              {React.createElement(getSystemIcon(system), { className: "h-4 w-4 text-muted-foreground" })}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dtcCodes.filter(dtc => dtc.system === system).length}
              </div>
              <p className="text-xs text-muted-foreground">{system} related</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  placeholder="Search DTC codes by code, description, or system..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="w-48">
              <Select value={systemFilter} onValueChange={setSystemFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by system" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Systems</SelectItem>
                  {systemCategories.map(system => (
                    <SelectItem key={system} value={system}>{system}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DTC Codes Grid */}
      <div className="grid gap-4">
        {filteredCodes.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <Code className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No DTC Codes Found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || systemFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Start by adding your first DTC code'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredCodes.map((dtc) => {
            const SystemIcon = getSystemIcon(dtc.system);
            return (
              <Card key={dtc.code} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <SystemIcon className="h-5 w-5 text-gray-500" />
                          <span className="text-xl font-bold text-blue-600">{dtc.code}</span>
                        </div>
                        <Badge className={getSystemColor(dtc.system)}>
                          {dtc.system}
                        </Badge>
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">{dtc.description}</h3>
                        
                        {dtc.symptoms && (
                          <div className="mb-2">
                            <p className="text-sm font-medium text-gray-700">Symptoms:</p>
                            <p className="text-sm text-gray-600">{dtc.symptoms}</p>
                          </div>
                        )}
                        
                        {dtc.possible_causes && (
                          <div className="mb-2">
                            <p className="text-sm font-medium text-gray-700">Possible Causes:</p>
                            <p className="text-sm text-gray-600">{dtc.possible_causes}</p>
                          </div>
                        )}
                        
                        {dtc.recommended_actions && (
                          <div>
                            <p className="text-sm font-medium text-gray-700">Recommended Actions:</p>
                            <p className="text-sm text-gray-600">{dtc.recommended_actions}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteDTC(dtc.code)}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DTCManagementSection;